import React, { useState } from 'react';
import { useSettings } from '../../../hooks/useSettings';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useFeatureFlag } from '../../../hooks/FeatureFlagsProvider';
import { isRetailMediaBlockedLocale } from '../../../../../lib/retailMedia';
import { Vendor, RetailMediaProps } from './types';
import {
  CUSTOM_BRANDING_FLAG,
  DiscoLayout,
  isCustomBrandingArm,
} from './vendors/Disco/constants';
import Thanks from './vendors/Thanks';
import Disco from './vendors/Disco';
import Fetch from './vendors/Fetch';
import { fetchOrderKey, isFetchEligible } from './vendors/Fetch/fetchHistory';
import { useFetchVisit } from './vendors/Fetch/useFetchVisit';
import {
  markDrawerOpened,
  wasDrawerOpened,
} from './vendors/Disco/drawerHistory';

const normalizeVendor = (value: string | null | undefined): Vendor | null => {
  const upper = value?.toUpperCase();
  if (upper === Vendor.Disco) return Vendor.Disco;
  if (upper === Vendor.Thanks) return Vendor.Thanks;
  if (upper === Vendor.Fetch) return Vendor.Fetch;
  return null;
};

function RetailMedia({
  fixedPanelHeight,
  appsJwt,
  decision,
  visitId,
  visitNumber,
  suppressed = false,
}: RetailMediaProps) {
  const { retailMedia } = useSettings();
  const customBrandingArm = useFeatureFlag(CUSTOM_BRANDING_FLAG);
  const {
    state: {
      deviceType,
      localeLanguage,
      localeCountry,
      orderNumbers,
      trackingNumbers,
    },
  } = usePageContext();

  const orderKey = fetchOrderKey(orderNumbers, trackingNumbers);
  const [drawerAlreadyOpened] = useState(() => wasDrawerOpened(orderKey));

  const blocked =
    suppressed ||
    isRetailMediaBlockedLocale(`${localeLanguage}_${localeCountry}`);

  const decisionVendorNormalized = normalizeVendor(decision?.vendor);
  const settingsVendorNormalized = normalizeVendor(retailMedia?.provider);

  // When the gateway answered, its answer is final: a null vendor means it
  // chose none, and reaching for the configured provider would put back what it
  // left out. The provider stands in only when there was no decision at all,
  // and never as Fetch, which is shown only on targeting's say-so.
  const selectedVendor = decision
    ? decisionVendorNormalized
    : settingsVendorNormalized === Vendor.Fetch
      ? null
      : (settingsVendorNormalized ?? Vendor.Disco);

  // A visit only counts where the offer can actually render, which needs the
  // JWT as much as the vendor -- the gateway failing to answer leaves us with
  // the configured vendor and no JWT.
  const fetchSelected =
    !blocked && selectedVendor === Vendor.Fetch && !!appsJwt;
  const fetchHistory = useFetchVisit(
    fetchSelected ? orderKey : undefined,
    visitId,
  );

  if (blocked) {
    return null;
  }

  // Rendering before the browser-held cap is readable would show Fetch to
  // shoppers whose visits are already spent.
  if (fetchSelected && !fetchHistory) {
    return null;
  }

  const fetchSpent = !!fetchHistory && !isFetchEligible(fetchHistory);
  const fallbackVendor = normalizeVendor(decision?.fallbackVendor);
  const vendor = fetchSelected && fetchSpent ? fallbackVendor : selectedVendor;
  const isFallback = fetchSelected && fetchSpent;
  const decisionId = decision?.decisionId;

  if (vendor === Vendor.Fetch) {
    if (!appsJwt) return null;
    return (
      <Fetch
        appsJwt={appsJwt}
        fixedPanelHeight={fixedPanelHeight}
        decisionId={decisionId}
      />
    );
  }

  if (vendor === Vendor.Thanks) {
    const thanksLayout = decision?.layouts?.thanks ?? 'COVER_IMAGE';
    return (
      <Thanks
        layout={thanksLayout === 'COVER_IMAGE' ? 'COVER_IMAGE' : 'INLINE'}
        decisionId={decisionId}
        isFallback={isFallback}
      />
    );
  }

  if (vendor !== Vendor.Disco || !appsJwt) {
    return null;
  }

  const isMobile = deviceType === 'mobile';
  const isInlineAlways = decision?.layouts?.disco === 'INLINE_ALWAYS';
  const isCoverImage =
    decision?.layouts?.disco === 'COVER_IMAGE' ||
    (isMobile && isCustomBrandingArm(customBrandingArm) && !isInlineAlways);

  const layout = isInlineAlways
    ? DiscoLayout.Inline
    : isMobile
      ? DiscoLayout.Drawer
      : isCoverImage
        ? DiscoLayout.Cover
        : DiscoLayout.Inline;
  const isDrawer = layout === DiscoLayout.Drawer;

  return (
    <Disco
      jwt={appsJwt}
      layout={layout}
      isCoverImage={isCoverImage}
      fixedPanelHeight={fixedPanelHeight}
      decisionId={decisionId}
      isFallback={isFallback}
      initiallyDismissed={isDrawer && drawerAlreadyOpened}
      onDrawerOpen={isDrawer ? () => markDrawerOpened(orderKey) : undefined}
      visitNumber={visitNumber}
      visitId={visitId}
    />
  );
}

export default RetailMedia;
